import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import newsService from '../services/newsService';
import './News.css';

function News({ type = 'combined', limit = 6, showTitle = true }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      switch (type) {
        case 'pharmaceutical':
          response = await newsService.getPharmaceuticalNews(limit);
          break;
        case 'health':
          response = await newsService.getHealthNews(limit);
          break;
        case 'combined':
        default:
          response = await newsService.getCombinedNews(limit);
          break;
      }

      setNews(response.data || []);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [type, limit]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="news-section">
        {showTitle && <h2 className="news-title">📰 Latest News</h2>}
        <div className="news-loading">
          <div className="loading-spinner"></div>
          <p>Loading latest news...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="news-section">
        {showTitle && <h2 className="news-title">📰 Latest News</h2>}
        <div className="news-error">
          <p>{error}</p>
          <button onClick={fetchNews} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (news.length === 0) {
    return (
      <div className="news-section">
        {showTitle && <h2 className="news-title">📰 Latest News</h2>}
        <div className="no-news">
          <p>No news articles available at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="news-section">
      {showTitle && <h2 className="news-title">📰 Latest News</h2>}

      <div className="news-grid">
        {news.map((article, index) => {
          const articleId = `article_${index}`;
          
          return (
            <Link
              key={index}
              to={`/news/${articleId}`}
              className="news-card"
              data-category={article.category}
              onClick={() => {
                // Store article data when clicking
                sessionStorage.setItem(`news_${articleId}`, JSON.stringify({
                  ...article,
                  category: type === 'pharmaceutical' ? 'Pharmaceutical' : type === 'health' ? 'Health' : 'Health & Pharma'
                }));
              }}
            >
              {article.urlToImage && (
                <div className="news-image">
                  <img
                    src={article.urlToImage}
                    alt={article.title}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              <div className="news-content">
                {article.category && (
                  <span className="news-category-badge">{article.category}</span>
                )}
                
                <h3 className="news-headline">
                  {article.title}
                </h3>

                {article.description && (
                  <p className="news-description">
                    {article.description.length > 150
                      ? `${article.description.substring(0, 150)}...`
                      : article.description
                    }
                  </p>
                )}

                {article.tags && article.tags.length > 0 && (
                  <div className="news-tags-preview">
                    {article.tags.slice(0, 3).map((tag, tagIndex) => (
                      <span key={tagIndex} className="news-tag">{tag}</span>
                    ))}
                  </div>
                )}

                <div className="news-meta">
                  <span className="news-source">{article.source}</span>
                  <span className="news-date">{formatDate(article.publishedAt)}</span>
                  {article.readTime && (
                    <span className="news-read-time">📖 {article.readTime}</span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default News;